<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, onUnmounted, computed, watch, nextTick } from 'vue';
import { Motion } from 'motion-v';
import { useAiTools } from '@/composables/useAiTools';
import { Amplitude } from '@seede/amplitude';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import { loadUnicornStudio } from '@/lib/unicornStudio';
import { localizedPath, normalizeSiteLocale, type SiteLocale } from '@/lib/site-locale';
import { i18n, t } from '~/i18n';

type FooterTool = {
  _id?: string;
  slug?: string;
  language?: SiteLocale;
  sceneKeywords?: string;
  heroTitle?: string;
};

type UnicornScene = {
  destroy?: () => void;
  resize?: () => void;
};

const footerVisible = ref(false);
const footerRef = ref<HTMLElement | null>(null);
const footerShellRef = ref<HTMLElement | null>(null);

const unicornScene = ref<UnicornScene | null>(null);
let resizeObserver: ResizeObserver | null = null;
let unicornInitStarted = false;

const currentLocale = computed<SiteLocale>(() => normalizeSiteLocale(i18n.locale) ?? 'en');
const footerPath = (path: string) => localizedPath(currentLocale.value, path);

function formatFallbackToolLabel(value: string) {
  return value
    .trim()
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getFooterToolLabel(tool: FooterTool) {
  const fallbackSource =
    tool.sceneKeywords || tool.heroTitle || (tool.slug || '').replace(/[-_]+/g, ' ');
  return formatFallbackToolLabel(fallbackSource);
}

// Curated footer links (Sanity singleton footerSettings.designTools, order preserved)
const { fetchFooterTools } = useAiTools();
const { data: allTools } = useLazyAsyncData(
  () => `footer-tools:${currentLocale.value}`,
  async () => {
    try {
      const tools = await fetchFooterTools(currentLocale.value);
      return tools || [];
    } catch (error) {
      console.error('Error fetching tools for footer:', error);
      return [];
    }
  },
  {
    server: false,
    default: () => [],
    watch: [currentLocale],
  }
);

const primaryKeywords = computed(() => {
  return (allTools.value ?? []).flatMap((tool: FooterTool) => {
    if (!tool.slug) {
      return [];
    }

    const keyword = getFooterToolLabel(tool);
    const language = tool.language || currentLocale.value;
    return {
      keyword,
      slug: tool.slug,
      path: localizedPath(language, `/ai-design-tools/${tool.slug}`),
    };
  });
});

const pagePath = computed(() => (import.meta.client ? window.location.href : ''));

const copyrightText = computed(() =>
  t('landing_home.footer.copyright', { year: String(new Date().getFullYear()) })
);

const comingSoonTooltip = computed(() => t('landing_home.footer.tooltip.coming_soon'));

const trackFooterNav = (target: string, text: string, group: string) => {
  Amplitude.event('nav_link_click', {
    link_text: text,
    link_target: target,
    section: 'footer',
    link_group: group,
    page_path: pagePath.value,
  });
};

// Geometric shapes explosion effect
function createGeometricShapes(e: MouseEvent) {
  const shapes = ['square', 'triangle', 'circle', 'cross'];
  const numShapes = Math.floor(Math.random() * 4) + 5; // 5-8 shapes

  for (let i = 0; i < numShapes; i++) {
    const shape = shapes[Math.floor(Math.random() * shapes.length)];
    const size = Math.floor(Math.random() * 6) + 8; // 10-16px
    const angle = Math.random() * Math.PI * 2;
    const velocity = Math.random() * 150 + 100; // 100-250px
    const rotation = Math.random() * 720 - 360; // -360 to 360 degrees

    const element = document.createElement('div');
    element.className = `geometric-shape ${shape}`;
    element.style.width = `${size}px`;
    element.style.height = `${size}px`;
    element.style.left = `${e.clientX}px`;
    element.style.top = `${e.clientY}px`;
    element.style.setProperty('--tx', `${Math.cos(angle) * velocity}px`);
    element.style.setProperty('--ty', `${Math.sin(angle) * velocity}px`);
    element.style.setProperty('--rotation', `${rotation}deg`);

    document.body.appendChild(element);

    setTimeout(() => {
      element.remove();
    }, 1000);
  }
}

// Footer visibility observer
let footerObserver: IntersectionObserver | null = null;
onMounted(() => {
  footerObserver = new IntersectionObserver(
    ([entry]: IntersectionObserverEntry[]) => {
      if (entry?.isIntersecting) {
        footerVisible.value = true;
        footerObserver?.disconnect();
      }
    },
    { threshold: 0.05 }
  );
  if (footerRef.value) footerObserver.observe(footerRef.value);
});

function scheduleUnicornResize() {
  try {
    unicornScene.value?.resize?.();
  } catch {
    /* ignore */
  }
}

async function initUnicorn() {
  if (unicornInitStarted || unicornScene.value) return;
  unicornInitStarted = true;

  try {
    const unicornStudio = await loadUnicornStudio();
    if (!document.getElementById('footer-unicorn-bg')) return;

    const scene = await unicornStudio.addScene({
      elementId: 'footer-unicorn-bg',
      fps: 60,
      scale: 0.7,
      dpi: 1.5,
      filePath: '/unicorn-footer-effect.json',
      lazyLoad: false,
      fixed: false,
      altText: 'Footer background effect',
      interactivity: {
        mouse: {
          disableMobile: false,
          disabled: false,
        },
      },
    });

    unicornScene.value = scene;
    await nextTick();
    scheduleUnicornResize();
  } catch (err: unknown) {
    console.warn('Failed to load Unicorn Studio effect:', err);
    unicornInitStarted = false;
  }
}

watch(footerVisible, (visible) => {
  if (visible) void initUnicorn();
});

watch([primaryKeywords, currentLocale], () => {
  void nextTick(scheduleUnicornResize);
});

onMounted(() => {
  if (footerShellRef.value) {
    resizeObserver = new ResizeObserver(() => {
      scheduleUnicornResize();
    });
    resizeObserver.observe(footerShellRef.value);
  }

  if (footerVisible.value) void initUnicorn();
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
});

onUnmounted(() => {
  footerObserver?.disconnect();
  if (unicornScene.value) {
    unicornScene.value.destroy?.();
    unicornScene.value = null;
  }
});
</script>

<template>
  <footer ref="footerRef" class="relative z-20" @click="createGeometricShapes">
    <h2 class="sr-only">{{ $t('landing_home.footer.sr_only') }}</h2>
    <div class="w-full overflow-hidden">
      <div ref="footerShellRef" class="relative bg-black p-6 md:px-14 py-16 overflow-hidden">
        <div
          id="footer-unicorn-bg"
          class="footer-unicorn-bg absolute inset-0 z-0 opacity-60 pointer-events-none rounded-[24px]"
        ></div>

        <div class="relative z-10 grid grid-cols-2 items-start lg:grid-cols-4 gap-8 lg:gap-8">
          <!-- Designs 列 - 移动端左侧 -->
          <Motion
            as="div"
            class="row-span-3 lg:row-span-1"
            :initial="{ opacity: 0, y: 24 }"
            :animate="footerVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }"
            :transition="{ duration: 0.7, delay: 0, ease: [0.22, 1, 0.36, 1] }"
          >
            <h3 class="text-sm font-normal mb-4 text-white/50 tracking-wide">
              {{ $t('landing_home.footer.col.design_tools') }}
            </h3>
            <ul class="space-y-4">
              <li v-for="item in primaryKeywords" :key="item.path">
                <NuxtLink
                  :to="item.path"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="trackFooterNav(item.path, item.keyword, 'design_tools')"
                >
                  {{ item.keyword }}
                </NuxtLink>
              </li>
            </ul>
          </Motion>

          <Motion
            as="div"
            :initial="{ opacity: 0, y: 24 }"
            :animate="footerVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }"
            :transition="{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }"
          >
            <h3 class="text-sm font-normal mb-4 text-white/50 tracking-wide">
              {{ $t('landing_home.footer.col.resources') }}
            </h3>
            <ul class="space-y-4">
              <li>
                <NuxtLink
                  :to="footerPath('/help')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="trackFooterNav('/help', t('landing_home.footer.link.help'), 'resources')"
                >
                  {{ $t('landing_home.footer.link.help') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="footerPath('/solutions')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="
                    trackFooterNav(
                      '/solutions',
                      t('landing_home.footer.link.solutions'),
                      'resources'
                    )
                  "
                >
                  {{ $t('landing_home.footer.link.solutions') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="footerPath('/ai-design-tools')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="
                    trackFooterNav(
                      '/ai-design-tools',
                      t('landing_home.footer.link.tools'),
                      'resources'
                    )
                  "
                >
                  {{ $t('landing_home.footer.link.tools') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="footerPath('/learn')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="
                    trackFooterNav('/learn', t('landing_home.footer.link.learn'), 'resources')
                  "
                >
                  {{ $t('landing_home.footer.link.learn') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="footerPath('/blog')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="trackFooterNav('/blog', t('landing_home.footer.link.blog'), 'resources')"
                >
                  {{ $t('landing_home.footer.link.blog') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="footerPath('/spotlight')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="
                    trackFooterNav(
                      '/spotlight',
                      t('landing_home.footer.link.spotlight'),
                      'resources'
                    )
                  "
                >
                  {{ $t('landing_home.footer.link.spotlight') }}
                </NuxtLink>
              </li>
              <li>
                <span
                  :title="comingSoonTooltip"
                  class="text-sm text-white/50 font-normal coming-soon-link"
                  >{{ $t('landing_home.footer.link.changelog') }}</span
                >
              </li>
              <li>
                <span
                  :title="comingSoonTooltip"
                  class="text-sm text-white/50 font-normal coming-soon-link"
                  >{{ $t('landing_home.footer.link.affiliates') }}</span
                >
              </li>
            </ul>
          </Motion>

          <Motion
            as="div"
            :initial="{ opacity: 0, y: 24 }"
            :animate="footerVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }"
            :transition="{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }"
          >
            <h3 class="text-sm font-normal mb-4 text-white/50 tracking-wide">
              {{ $t('landing_home.footer.col.company') }}
            </h3>
            <ul class="space-y-4">
              <li>
                <NuxtLink
                  :to="footerPath('/about')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="trackFooterNav('/about', t('landing_home.footer.link.about'), 'company')"
                >
                  {{ $t('landing_home.footer.link.about') }}
                </NuxtLink>
              </li>

              <li>
                <NuxtLink
                  :to="footerPath('/contact')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="
                    trackFooterNav('/contact', t('landing_home.footer.link.contact'), 'company')
                  "
                >
                  {{ $t('landing_home.footer.link.contact') }}
                </NuxtLink>
              </li>

              <li>
                <NuxtLink
                  :to="footerPath('/terms')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="trackFooterNav('/terms', t('landing_home.footer.link.terms'), 'company')"
                >
                  {{ $t('landing_home.footer.link.terms') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="footerPath('/privacy')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="
                    trackFooterNav('/privacy', t('landing_home.footer.link.privacy'), 'company')
                  "
                >
                  {{ $t('landing_home.footer.link.privacy') }}
                </NuxtLink>
              </li>
              <li>
                <NuxtLink
                  :to="footerPath('/cookie-policy')"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 block font-normal"
                  @click="
                    trackFooterNav(
                      '/cookie-policy',
                      t('landing_home.footer.link.cookie_policy'),
                      'company'
                    )
                  "
                >
                  {{ $t('landing_home.footer.link.cookie_policy') }}
                </NuxtLink>
              </li>
            </ul>
          </Motion>

          <Motion
            as="div"
            :initial="{ opacity: 0, y: 24 }"
            :animate="footerVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }"
            :transition="{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }"
          >
            <h3 class="text-sm font-normal mb-4 text-white/50 tracking-wide">
              {{ $t('landing_home.footer.col.connect') }}
            </h3>
            <ul class="space-y-4">
              <li>
                <a
                  href="https://x.com/veesoai"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 flex items-center gap-2 font-normal"
                  @click="
                    trackFooterNav(
                      'https://x.com/veesoai',
                      t('landing_home.footer.link.x'),
                      'connect'
                    )
                  "
                >
                  <span>{{ $t('landing_home.footer.link.x') }}</span>
                </a>
              </li>
              <li>
                <a
                  href="https://www.linkedin.com/company/veeso-ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 flex items-center gap-2 font-normal"
                  @click="
                    trackFooterNav(
                      'https://www.linkedin.com/company/veeso-ai',
                      t('landing_home.footer.link.linkedin'),
                      'connect'
                    )
                  "
                >
                  <span>{{ $t('landing_home.footer.link.linkedin') }}</span>
                </a>
              </li>
              <li>
                <a
                  href="https://discord.gg/twbZEHkuWx"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-sm text-white/80 hover:text-white transition-colors duration-200 flex items-center gap-2 font-normal"
                  @click="
                    trackFooterNav(
                      'https://discord.gg/twbZEHkuWx',
                      t('landing_home.footer.link.discord'),
                      'connect'
                    )
                  "
                >
                  <span>{{ $t('landing_home.footer.link.discord') }}</span>
                </a>
              </li>
            </ul>
          </Motion>
        </div>

        <Motion
          as="div"
          class="relative z-10 mt-16 pt-16"
          :initial="{ opacity: 0, y: 16 }"
          :animate="footerVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }"
          :transition="{ duration: 0.7, delay: 0.4, ease: [0.22, 1, 0.36, 1] }"
        >
          <div class="relative flex flex-row items-center justify-between gap-2 md:gap-4">
            <p class="text-xs md:text-sm text-white/50 font-normal flex-1">
              {{ copyrightText }}
            </p>

            <div
              class="absolute left-1/2 top-1/2 flex shrink-0 -translate-x-1/2 -translate-y-1/2 items-center justify-center group cursor-pointer"
            >
              <svg
                width="20"
                height="22"
                viewBox="0 0 32 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="footer-logo block h-[22px] w-5 shrink-0"
              >
                <defs>
                  <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stop-color="#60a5fa" />
                    <stop offset="25%" stop-color="#a78bfa" />
                    <stop offset="50%" stop-color="#ec4899" />
                    <stop offset="75%" stop-color="#f97316" />
                    <stop offset="100%" stop-color="#eab308" />
                  </linearGradient>
                </defs>
                <path
                  d="M26.1904 13.3808C27.8786 13.851 29.2868 14.8531 30.2217 16.5039C33.9933 23.1639 29.619 30.7226 20.3975 34.3955C10.7276 38.2467 0.501269 33.9736 0.279297 33.8593C0.143091 33.789 0.0652346 33.6179 0.0830078 33.4843C0.103487 33.3241 3.11189 27.2784 7.87988 21.3974C7.96532 21.2916 8.13371 21.3258 8.18457 21.4511C8.25402 21.6221 8.33407 21.7938 8.42578 21.9638C10.1542 25.1589 15.9693 24.0823 19.4951 20.9189C22.3639 18.6805 25.3883 14.197 25.9395 13.4677V13.4668C25.9982 13.3902 26.097 13.355 26.1904 13.3808ZM11.1787 1.60544C20.8499 -2.24616 31.0778 2.0276 31.2969 2.14157C31.5156 2.2555 31.5265 2.40711 31.458 2.60641C31.458 2.60641 27.1427 10.3413 23.9756 14.2715C23.948 14.3062 23.9087 14.3548 23.873 14.3984C23.8214 14.4605 23.7348 14.482 23.6602 14.4482C23.5079 14.3805 23.2482 14.2643 23.0391 14.1806C21.3171 13.4906 17.7361 12.42 14.1113 13.5009C10.4483 14.5934 7.97492 17.034 7.86719 19.6357C7.86264 19.7371 7.82237 19.8361 7.75391 19.9072C7.68544 19.9783 7.61748 20.05 7.55078 20.122C6.79574 20.9403 6.10147 21.7433 5.46484 22.5215C5.41586 22.581 5.33545 22.6062 5.26074 22.584C3.62782 22.0996 2.26525 21.1068 1.35352 19.497C-2.41787 12.8371 1.95716 5.27821 11.1787 1.60544Z"
                  class="logo-path"
                />
              </svg>
            </div>

            <div class="relative flex flex-1 justify-end">
              <LanguageSwitcher mode="button" variant="dark" />
            </div>
          </div>
        </Motion>
      </div>
    </div>
  </footer>
</template>

<style>
.geometric-shape {
  position: fixed;
  pointer-events: none;
  border: 1px solid #fff;
  background: transparent;
  animation: explode 1s ease-out forwards;
  z-index: 9999;
}

/* Square is just a div with border - no additional styles needed */

.geometric-shape.circle {
  border-radius: 50%;
}

.geometric-shape.triangle {
  border: none;
  width: 0 !important;
  height: 0 !important;
  border-left: 16px solid transparent;
  border-right: 16px solid transparent;
  border-bottom: 28px solid #fff;
  background: transparent;
}

.geometric-shape.cross {
  position: relative;
}

.geometric-shape.cross::before,
.geometric-shape.cross::after {
  content: '';
  position: absolute;
  background: #fff;
}

.geometric-shape.cross::before {
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  transform: translateX(-50%);
}

.geometric-shape.cross::after {
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  transform: translateY(-50%);
}

@keyframes explode {
  0% {
    transform: translate(0, 0) rotate(0deg) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(var(--tx), var(--ty)) rotate(var(--rotation)) scale(0.3);
    opacity: 0;
  }
}

/* Coming soon links */
.coming-soon-link {
  display: block;
  cursor: not-allowed;
}

/* Unicorn 背景：容器尺寸变化时由 ResizeObserver 触发 scene.resize() */
.footer-unicorn-bg {
  overflow: hidden;
}

.footer-unicorn-bg canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

/* Footer logo styles */
.footer-logo {
  opacity: 0.5;
  transition: opacity 0.3s ease;
}

.footer-logo .logo-path {
  fill: white;
  fill-opacity: 0.5;
  transition: fill 0.3s ease;
}

.group:hover .footer-logo {
  opacity: 1;
}

.group:hover .footer-logo .logo-path {
  fill: url(#logo-gradient);
  fill-opacity: 1;
  animation: gradient-flow 3s ease-in-out infinite;
}

@keyframes gradient-flow {
  0%,
  100% {
    filter: hue-rotate(0deg);
  }
  50% {
    filter: hue-rotate(60deg);
  }
}
</style>
